/* eslint-disable @typescript-eslint/naming-convention */
import { gql } from "@apollo/client"
import { Text, type useToast } from "@chakra-ui/react"
import passwordValidator from "password-validator"
import { USER_NOT_VERIFIED } from "../../../common/commonConstants"
import { type SignupPageAtomInterface } from "../../../components/commons/commonTypes"

export const linkButtonProps = {
  variant: "link",
  h: "18px",
  color: "white",
  fontWeight: "bold"
}
interface checkIfFormInputsAreNotEmptyProps {
  isValid: boolean
  message: string
}
const checkIfFormInputsAreNotEmpty = (form: HTMLFormElement | null, toast: ReturnType<typeof useToast>): checkIfFormInputsAreNotEmptyProps => {
  const elements = form?.elements as HTMLFormControlsCollection
  interface handleValidationResultInterface {
    isValid: boolean
    message: string
  }
  interface handleValidationResultProps {
    isValid: boolean
    message: string
  }
  const handleValidationResult = (result: handleValidationResultProps, element: HTMLInputElement): handleValidationResultInterface => {
    if (!result.isValid) {
      element.focus()
      failForm(result?.message, toast)
    }
    return result
  }
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i] as HTMLInputElement
    if (element.getAttribute("required") !== null) {
      const result = handleValidationResult(validateEmptyInput(element.value), element)
      if (!result.isValid) {
        return result
      }
    }
    if (element.getAttribute("type") === "email") {
      const result = handleValidationResult(validateEmail(element.value), element)
      if (!result.isValid) {
        return result
      }
    }
  }
  return {
    isValid: true,
    message: ""
  }
}

export const failForm = (errorMessage: string, toast: ReturnType<typeof useToast>): void => {
  const id = errorMessage.replaceAll(" ", "-")
  if (errorMessage === USER_NOT_VERIFIED) {
    return
  }

  if (!toast.isActive(id)) {
    toast({
      id,
      title: "Error",
      status: "error",
      description: <Text>{errorMessage}</Text>,
      isClosable: true,
      duration: 4000
    })
  }
}
interface testPasswordInterface {
  isValid: boolean
  message: string
}
export const validateRequiredForm = (
  form: HTMLFormElement | null,
  getSignupPageAtom: SignupPageAtomInterface,
  termsAndConditionsCheck: boolean,
  toast: ReturnType<typeof useToast>
): testPasswordInterface => {
  const { email_address, re_enter_email, password, re_enter_password } = getSignupPageAtom
  const emailVerify = validateEmail(email_address ?? "")
  if (!emailVerify.isValid) {
    failForm(emailVerify.message, toast)
    return {
      isValid: false,
      message: emailVerify.message
    }
  }

  // Checks if form inputs have required attributes and are empty
  const result = checkIfFormInputsAreNotEmpty(form, toast)

  const testPassword = (value: string): testPasswordInterface => {
    const testPassword = validatePassword(value)
    if (!testPassword.isValid) {
      failForm(testPassword.message, toast)
      return {
        isValid: false,
        message: testPassword.message
      }
    }
    return {
      isValid: true,
      message: ""
    }
  }
  if (!result.isValid) {
    return result
  } else {
    if (email_address !== re_enter_email) {
      form?.email_address.focus()
      failForm("Emails aren't matcing.", toast)
      return {
        isValid: false,
        message: "Emails aren't matching."
      }
    }
    if (password !== re_enter_password) {
      form?.password.focus()
      failForm("Passwords do not match.", toast)
      return {
        isValid: false,
        message: "Passwords do not match"
      }
    }
    if (!termsAndConditionsCheck) {
      failForm("Please read Terms and Conditions.", toast)
      return {
        isValid: false,
        message: "Please read Terms and Conditions."
      }
    }
    if (password) {
      const result = testPassword(password)
      if (!result.isValid) {
        form?.password.focus()
        return result
      }
    }
    if (re_enter_password) {
      const result = testPassword(re_enter_password)
      if (!result.isValid) {
        return result
      }
    }
  }

  return {
    isValid: true,
    message: ""
  }
}

export const validateEmptyInput = (value: string): testPasswordInterface => {
  if (value === "") {
    return {
      isValid: false,
      message: "This field is required."
    }
  }
  return {
    isValid: true,
    message: ""
  }
}
export const validateEmail = (email: string): testPasswordInterface => {
  // const re = /\S+@\S+\.\S+/
  const re = /^[-!#$%&'*+/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/

  if (email && !re.test(email)) {
    return {
      isValid: false,
      message: "Please enter a valid email address."
    }
  } else {
    return {
      isValid: true,
      message: ""
    }
  }
}
export const validatePassword = (pass: string): testPasswordInterface => {
  // eslint-disable-next-line new-cap
  const schema = new passwordValidator()
  schema
    .is()
    .min(8, " minimum 8 characters") // Minimum length 8
    .is()
    .max(100, " maximum 100 characters") // Maximum length 100
    .has()
    .uppercase(1) // Must have at least 1 upper case letter
    .has()
    .lowercase(1) // Must have at least 1 lower case letter
    .has()
    .digits(1) // Must have at least 1 digits
    .has()
    .not()
    .spaces() // Should not have spaces
    .is()
    .not()
    .oneOf(["Passw0rd", "Password123", "password"]) // Blacklist these values
  const validation = schema.validate(pass, {
    details: true
  })

  let error = ""
  let spaceErr = false
  if (Array.isArray(validation) && validation.length > 0) {
    validation.forEach(err => {
      if (err.message.includes("spaces")) {
        spaceErr = true
      } else {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        error += err.message.replace("The string should have a", "") + ", "
      }
    })
    error = "The password should have " + (spaceErr ? error + " should not have space" : error.substring(0, error.length - 2)) + "."
  }
  if (Array.isArray(validation) && validation.length === 0) {
    return {
      isValid: true,
      message: error
    }
  } else {
    return {
      isValid: false,
      message: error
    }
  }
}
export const REGISTER = gql`
  mutation Signup(
    $first_name: String!
    $last_name: String
    $email_address: String!
    $password: String!
    $company_name: String
    $address1: String
    $address2: String
    $suburb: String
    $city: String
    $state: String
    $pincode: String
    $company_logo: String
    $supplier: String
  ) {
    signup(
      first_name: $first_name
      last_name: $last_name
      email_address: $email_address
      password: $password
      company_name: $company_name
      address1: $address1
      address2: $address2
      suburb: $suburb
      city: $city
      state: $state
      pincode: $pincode
      company_logo: $company_logo
      supplier: $supplier
    )
  }
`

export const UPDATE_USERPROFILE =
  gql(`mutation UpdateProfile($user_id: Int!, $first_name: String, $last_name: String, $email_address: String, $new_email_address: String, $password: String, $new_password: String, $company_name: String, $address1: String, $address2: String, $suburb: String, $city: String, $state: String, $pincode: String, $company_logo: String, $supplier: String) {
  updateProfile(user_id: $user_id, first_name: $first_name, last_name: $last_name, email_address: $email_address, new_email_address: $new_email_address, password: $password, new_password: $new_password, company_name: $company_name, address1: $address1, address2: $address2, suburb: $suburb, city: $city, state: $state, pincode: $pincode, company_logo: $company_logo, supplier: $supplier)
}`)
